import Phaser from 'phaser';
import { LAYER } from './depth';

/**
 * Shared combat feedback (Phase 6): a soft particle texture, short-lived
 * flashes drawn into one Graphics object, and batched floating damage
 * numbers. Purely cosmetic — reads nothing from the simulation.
 */
export const FX_PUFF_KEY = 'fx-puff';

export function generateFxArt(scene: Phaser.Scene): void {
  if (scene.textures.exists(FX_PUFF_KEY)) return;
  const tex = scene.textures.createCanvas(FX_PUFF_KEY, 32, 32);
  if (!tex) throw new Error('Could not create fx texture');
  const ctx = tex.getContext();
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  tex.refresh();
}

type Flash =
  | { kind: 'dot'; x: number; y: number; r: number; color: number; until: number; life: number }
  | { kind: 'slash'; x: number; y: number; until: number; life: number }
  | { kind: 'ring'; x: number; y: number; r: number; color: number; until: number; life: number };

/** Brief flashes (muzzle, impact spark, melee slash), redrawn each frame and dropped when expired. */
export class Flashes {
  private readonly g: Phaser.GameObjects.Graphics;
  private list: Flash[] = [];

  constructor(private readonly scene: Phaser.Scene) {
    this.g = scene.add.graphics().setDepth(LAYER.overlay - 15);
  }

  get count(): number {
    return this.list.length;
  }

  dot(x: number, y: number, r: number, color: number, ms = 90): void {
    if (this.list.length > 200) return;
    this.list.push({ kind: 'dot', x, y, r, color, until: this.scene.time.now + ms, life: ms });
  }

  ring(x: number, y: number, r: number, color: number, ms = 220): void {
    if (this.list.length > 200) return;
    this.list.push({ kind: 'ring', x, y, r, color, until: this.scene.time.now + ms, life: ms });
  }

  slash(x: number, y: number, ms = 140): void {
    if (this.list.length > 200) return;
    this.list.push({ kind: 'slash', x, y, until: this.scene.time.now + ms, life: ms });
  }

  draw(): void {
    const now = this.scene.time.now;
    this.list = this.list.filter((f) => f.until > now);
    this.g.clear();
    for (const f of this.list) {
      const k = Math.max(0, (f.until - now) / f.life); // 1 → 0
      if (f.kind === 'dot') {
        this.g.fillStyle(f.color, 0.95 * k);
        this.g.fillCircle(f.x, f.y, f.r * (0.6 + 0.4 * k));
      } else if (f.kind === 'ring') {
        this.g.lineStyle(2, f.color, 0.9 * k);
        this.g.strokeCircle(f.x, f.y, f.r * (1.6 - 0.6 * k));
      } else {
        this.g.lineStyle(2.5, 0xffffff, 0.9 * k);
        this.g.beginPath();
        this.g.arc(f.x, f.y, 9, Phaser.Math.DegToRad(200), Phaser.Math.DegToRad(320));
        this.g.strokePath();
      }
    }
  }

  destroy(): void {
    this.g.destroy();
    this.list = [];
  }
}

/**
 * Floating damage numbers. Hits on the same thing within `windowMs` are summed
 * into one number (so a squad of Rangers shows "−24", not eight "−3"s), and
 * the number of live labels is capped — readable on a phone, cheap on any device.
 */
export class DamageNumbers {
  private readonly pending = new Map<string, { x: number; y: number; amount: number; color: string; due: number }>();
  private readonly live = new Set<Phaser.GameObjects.Text>();
  private static readonly WINDOW_MS = 350;
  private static readonly MAX_LIVE = 24;

  constructor(private readonly scene: Phaser.Scene) {}

  get count(): number {
    return this.live.size;
  }

  add(key: string, x: number, y: number, amount: number, color = '#ff8f7a'): void {
    if (!(amount > 0)) return;
    const p = this.pending.get(key);
    if (p) {
      p.amount += amount;
      p.x = x;
      p.y = y;
    } else {
      this.pending.set(key, { x, y, amount, color, due: this.scene.time.now + DamageNumbers.WINDOW_MS });
    }
  }

  update(): void {
    const now = this.scene.time.now;
    for (const [key, p] of this.pending) {
      if (p.due > now) continue;
      this.pending.delete(key);
      if (this.live.size >= DamageNumbers.MAX_LIVE) continue;
      const t = this.scene.add
        .text(p.x, p.y, `−${p.amount}`, {
          fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif",
          fontStyle: '700',
          fontSize: '15px',
          color: p.color,
          stroke: '#101214',
          strokeThickness: 3,
          resolution: 2,
        })
        .setOrigin(0.5, 1)
        .setDepth(LAYER.overlay + 4);
      this.live.add(t);
      this.scene.tweens.add({
        targets: t,
        y: t.y - 18,
        alpha: { from: 1, to: 0 },
        ease: 'Cubic.easeOut',
        duration: 800,
        onComplete: () => {
          this.live.delete(t);
          t.destroy();
        },
      });
    }
  }

  destroy(): void {
    for (const t of this.live) t.destroy();
    this.live.clear();
    this.pending.clear();
  }
}
